import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import usePageTracking from '../hooks/usePageTracking';

// ============================================================================
// CRITICAL IMPORTS (Loaded immediately for landing page)
// These are needed for the landing page - keep minimal!
// ============================================================================
import WaitlistLanding from '../pages/WaitlistLanding';

// ============================================================================
// LAZY LOADED COMPONENTS (Code-split, loaded on demand)
// This reduces initial bundle size from 234KB to ~80-100KB for landing page
// ============================================================================

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-orange-50 to-pink-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-stone-500">Loading...</p>
    </div>
  </div>
);

// Auth Pages (loaded when user clicks login/register)
const LoginPage = lazy(() => import('../pages/LoginPage'));
const NewSignupFlow = lazy(() => import('../pages/NewSignupFlow'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const OnboardingPage = lazy(() => import('../pages/OnboardingPageNew'));
const VerificationPending = lazy(() => import('../pages/VerificationPending'));
const WaitlistConfirmation = lazy(() => import('../pages/WaitlistConfirmation'));
const OAuthCallbackPage = lazy(() => import('../pages/OAuthCallbackPage'));

// Protected User Pages (loaded only after authentication)
const HomePage = lazy(() => import('../pages/HomePage'));
const ConnectionsPage = lazy(() => import('../pages/ConnectionsPage'));
const CreatePage = lazy(() => import('../pages/CreatePage'));
const DiscussionsPage = lazy(() => import('../pages/DiscussionsPage'));
const ManageEventsPage = lazy(() => import('../pages/ManageEventsPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const MessagesPage = lazy(() => import('../pages/MessagesPage'));
const ChatPage = lazy(() => import('../pages/ChatPage'));
const EventDetailPage = lazy(() => import('../pages/EventDetailPage'));
const FriendRequestsPage = lazy(() => import('../pages/FriendRequestsPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const PublicProfilePage = lazy(() => import('../pages/PublicProfilePage'));
const PearlActivityPage = lazy(() => import('../pages/PearlActivityPage'));
const PastEventsPage = lazy(() => import('../pages/PastEventsPage'));

// Admin Pages (loaded only when accessing admin routes)
const AdminLoginPage = lazy(() => import('../pages/AdminLoginPage'));
const AdminDashboard = lazy(() => import('../pages/AdminDashboard'));
const AdminWaitlist = lazy(() => import('../pages/AdminWaitlist'));
const AdminModeration = lazy(() => import('../pages/AdminModeration'));

// Notification Listener (lazy loaded, only for authenticated users)
const NotificationListener = lazy(() => import('./NotificationListener'));

/**
 * AppRoutes Component
 * 
 * CRITICAL: This component MUST be inside <BrowserRouter> because
 * it uses usePageTracking() which depends on useLocation() from react-router
 * 
 * CODE SPLITTING STRATEGY:
 * - Landing page (WaitlistLanding) loads immediately
 * - All other pages are lazy-loaded on demand
 * - This reduces initial bundle from ~234KB to ~80-100KB gzipped
 */
const AppRoutes = ({ 
  isAuthenticated, 
  isAdminAuthenticated, 
  isLoading, 
  handleLogin, 
  handleLogout,
  handleAdminLogin,
  handleAdminLogout 
}) => {
  // 🎯 CRITICAL: Track page views on every route change for SPA
  // This hook MUST be called inside <BrowserRouter> context
  // This populates GA4 "Pages & Screens" report
  usePageTracking();

  // Loading state
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      {/* Global Notification Listener - only when authenticated */}
      {isAuthenticated && (
        <Suspense fallback={null}>
          <NotificationListener />
        </Suspense>
      )}
      
      <Routes>
        {/* ========== PUBLIC ROUTES ========== */}
        {/* Landing page - NOT lazy loaded for fastest initial load */}
        <Route path="/" element={<WaitlistLanding />} />
        <Route path="/waitlist" element={<WaitlistLanding />} />
        <Route path="/join" element={<WaitlistLanding />} />
        
        {/* Auth routes - lazy loaded */}
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/register" element={<NewSignupFlow onLogin={handleLogin} />} />
        <Route path="/auth" element={<Navigate to="/register" replace />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<OAuthCallbackPage onLogin={handleLogin} />} />
        <Route path="/onboarding" element={<OnboardingPage onComplete={handleLogin} />} />
        <Route path="/verification-pending" element={<VerificationPending />} />
        <Route path="/waitlist-confirmation" element={<WaitlistConfirmation />} />

        {/* ========== PROTECTED USER ROUTES ========== */}
        <Route
          path="/home"
          element={isAuthenticated ? <HomePage /> : <Navigate to="/login" />}
        />
        <Route
          path="/connections"
          element={isAuthenticated ? <ConnectionsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/create"
          element={isAuthenticated ? <CreatePage /> : <Navigate to="/login" />}
        />
        <Route
          path="/loop"
          element={isAuthenticated ? <DiscussionsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/discussions"
          element={<Navigate to="/loop" replace />}
        />
        <Route
          path="/manage-events"
          element={isAuthenticated ? <ManageEventsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/profile"
          element={isAuthenticated ? <ProfilePage onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/settings"
          element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/pearls"
          element={isAuthenticated ? <PearlActivityPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/past-events"
          element={isAuthenticated ? <PastEventsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/events/:id"
          element={isAuthenticated ? <EventDetailPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/messages"
          element={isAuthenticated ? <MessagesPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/messages/:id"
          element={isAuthenticated ? <ChatPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/friend-requests"
          element={isAuthenticated ? <FriendRequestsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/notifications"
          element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/user/:userId"
          element={isAuthenticated ? <PublicProfilePage /> : <Navigate to="/login" />}
        />

        {/* ========== ADMIN ROUTES ========== */}
        <Route
          path="/admin/login"
          element={isAdminAuthenticated ? <Navigate to="/admin/dashboard" /> : <AdminLoginPage onAdminLogin={handleAdminLogin} />}
        />
        <Route
          path="/admin/dashboard"
          element={isAdminAuthenticated ? <AdminDashboard onAdminLogout={handleAdminLogout} /> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/waitlist"
          element={isAdminAuthenticated ? <AdminWaitlist /> : <Navigate to="/admin/login" />}
        />
        <Route
          path="/admin/moderation"
          element={isAdminAuthenticated ? <AdminModeration /> : <Navigate to="/admin/login" />}
        />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
